import { Registro } from '../types';
import { categorizarIncidencia, esSinNovedad } from './incidenciasUtils';

// Interface para cache de categorización (para uso futuro)
interface CacheEntry {
  data: Registro[];
  timestamp: number;
  hash: string;
}

// Configuración de cache (para uso futuro)
const CACHE_CONFIG = {
  TTL: 30 * 60 * 1000, // 30 minutos
  KEY_PREFIX: 'seprovinsa_categorias_',
  VERSION: '1.0'
};

// Exportar para evitar advertencias de TypeScript
export type { CacheEntry };
export { CACHE_CONFIG }

/**
 * Auto-categoriza un registro si no tiene categoría asignada
 * Analiza el campo 'incidencia' y 'descripcion' para determinar la categoría
 */
export const autoCategorizar = (registro: Registro): Registro => {
  // Si ya tiene categoría válida, no hacer nada
  if (
    registro.categoria &&
    registro.categoria.trim() !== '' &&
    registro.categoria.toLowerCase() !== 'null'
  ) {
    return registro;
  }

  // Intentar categorizar desde el campo 'incidencia'
  let categoriaDetectada: string | null = null;

  if (registro.incidencia) {
    // Primero verificar si es "sin novedad"
    if (esSinNovedad(registro.incidencia)) {
      categoriaDetectada = 'Sin novedad';
    } else {
      // Intentar categorizar usando las reglas existentes
      categoriaDetectada = categorizarIncidencia(registro.incidencia);
    }
  }

  // Si no se detectó desde 'incidencia', intentar desde 'descripcion'
  if (!categoriaDetectada && registro.descripcion) {
    if (esSinNovedad(registro.descripcion)) {
      categoriaDetectada = 'Sin novedad';
    } else {
      categoriaDetectada = categorizarIncidencia(registro.descripcion);
    }
  }

  // Si no se detectó desde 'descripcion', intentar desde 'observaciones'
  if (!categoriaDetectada && registro.observaciones) {
    if (esSinNovedad(registro.observaciones)) {
      categoriaDetectada = 'Sin novedad';
    } else {
      categoriaDetectada = categorizarIncidencia(registro.observaciones);
    }
  }

  // Asignar la categoría detectada o dejar como estaba
  return {
    ...registro,
    categoria: categoriaDetectada || registro.categoria || undefined,
  };
};

/**
 * Auto-categoriza un array de registros
 */
export const autoCategorizarLote = (registros: Registro[]): Registro[] => {
  return registros.map(autoCategorizar);
};

/**
 * Obtiene estadísticas de categorización
 */
export const obtenerEstadisticasCategorizacion = (registros: Registro[]) => {
  const total = registros.length;
  const conCategoria = registros.filter(
    r => r.categoria && r.categoria.trim() !== '' && r.categoria.toLowerCase() !== 'null'
  ).length;
  const sinCategoria = total - conCategoria;

  // Simular auto-categorización para ver cuántos se pueden categorizar
  const categorizados = autoCategorizarLote(registros);
  const conCategoriaPostAuto = categorizados.filter(
    r => r.categoria && r.categoria.trim() !== '' && r.categoria.toLowerCase() !== 'null'
  ).length;
  const mejora = conCategoriaPostAuto - conCategoria;

  return {
    total,
    conCategoria,
    sinCategoria,
    porcentajeConCategoria: ((conCategoria / total) * 100).toFixed(1),
    mejoraConAutoCat: mejora,
    porcentajeMejora: ((mejora / total) * 100).toFixed(1),
  };
};

/**
 * Identifica registros que necesitan categorización manual
 * (aquellos que no pudieron ser auto-categorizados)
 */
export const obtenerRegistrosSinCategorizar = (registros: Registro[]): Registro[] => {
  const categorizados = autoCategorizarLote(registros);
  return categorizados.filter(
    r => !r.categoria || r.categoria.trim() === '' || r.categoria.toLowerCase() === 'null'
  );
};
